import Vue from 'vue';
import * as api from '@/api/methods/assistant';

// Live event streams by conversation id (kept outside of the reactive state).
const subscriptions = new Map();

const emptyThread = () => ({
  conversation: null,
  messages: [],
  live: null,
  context: null,
  canResume: false,
  loading: false,
  error: '',
});

const emptyLive = (messageId) => ({
  messageId,
  status: { phase: 'queue', text: 'Запрос принят' },
  steps: [],
  draft: '',
  progress: null,
  context: null,
});

function lastContext(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i].meta || {};
    if (meta.context && meta.context.limit) return meta.context;
  }
  return null;
}

const closeAll = () => {
  subscriptions.forEach((handle) => handle.close());
  subscriptions.clear();
};

function clearConversations(state) {
  closeAll();
  Object.assign(state, {
    lists: { chat: [], investigation: [] },
    listsLoaded: { chat: false, investigation: false },
    active: { chat: null, investigation: null },
    threads: {},
    evidence: {},
    computers: null,
  });
}

export default {
  namespaced: true,
  state: () => ({
    ownerId: null,
    open: false,
    mode: 'chat',
    capabilities: null,
    capabilitiesError: '',
    // Proxy problem code of the failed capabilities request: not-configured | unreachable | unauthorized.
    capabilitiesCode: '',
    status: null,
    lists: { chat: [], investigation: [] },
    listsLoaded: { chat: false, investigation: false },
    active: { chat: null, investigation: null },
    threads: {},
    evidence: {},
    computers: null,
    // Shared memory: entries by status ({active, pending, …}; only active for non-administrators).
    memoryCounts: null,
  }),

  getters: {
    activeId: (state) => state.active[state.mode],
    thread: (state, getters) => (getters.activeId ? state.threads[getters.activeId] || null : null),
    list: (state) => state.lists[state.mode],
    canConfigure: (state) =>
      !!((state.capabilities && state.capabilities.canConfigure) || (state.status && state.status.canConfigure)),
    // Side work detection: { available, canManage, newFindings } (administrators manage it).
    sidework: (state) => (state.capabilities && state.capabilities.sidework) || {},
    runningCount: (state) =>
      [...state.lists.chat, ...state.lists.investigation].filter((c) => c.status === 'running').length,
    evidenceFor: (state) => (id) => state.evidence[id] || {},
  },

  mutations: {
    SET(state, values) {
      Object.keys(values).forEach((key) => {
        state[key] = values[key];
      });
    },
    RESET(state, ownerId) {
      state.ownerId = ownerId;
      state.capabilities = null;
      state.capabilitiesError = '';
      state.capabilitiesCode = '';
      state.status = null;
      state.memoryCounts = null;
      clearConversations(state);
    },
    SET_LIST(state, { mode, list }) {
      state.lists[mode] = list;
      state.listsLoaded[mode] = true;
    },
    UPSERT_CONVERSATION(state, conversation) {
      if (!conversation) return;
      const list = state.lists[conversation.mode] || state.lists.chat;
      const index = list.findIndex((c) => c.id === conversation.id);
      if (index >= 0) list.splice(index, 1);
      list.unshift(conversation);
      const thread = state.threads[conversation.id];
      if (thread) thread.conversation = conversation;
    },
    // A known conversation changes in place (status, title); a new one is added on top.
    UPSERT_LIST_ITEM(state, conversation) {
      if (!conversation) return;
      const list = state.lists[conversation.mode] || state.lists.chat;
      const index = list.findIndex((c) => c.id === conversation.id);
      if (index >= 0) list.splice(index, 1, { ...list[index], ...conversation });
      else list.unshift(conversation);
      const thread = state.threads[conversation.id];
      if (thread) thread.conversation = { ...(thread.conversation || {}), ...conversation };
    },
    REMOVE_CONVERSATION(state, id) {
      ['chat', 'investigation'].forEach((mode) => {
        state.lists[mode] = state.lists[mode].filter((c) => c.id !== id);
        if (state.active[mode] === id) state.active[mode] = null;
      });
      Vue.delete(state.threads, id);
      Vue.delete(state.evidence, id);
    },
    SET_ACTIVE(state, { mode, id }) {
      state.active[mode] = id;
    },
    ENSURE_THREAD(state, id) {
      if (!state.threads[id]) Vue.set(state.threads, id, emptyThread());
    },
    PATCH_THREAD(state, { id, patch }) {
      const thread = state.threads[id];
      if (thread) Object.assign(thread, patch);
    },
    LOAD_THREAD(state, { id, data }) {
      const thread = state.threads[id] || emptyThread();
      thread.conversation = data.conversation;
      thread.messages = data.messages || [];
      thread.live = data.live ? { ...emptyLive(data.live.messageId), ...data.live } : null;
      thread.context = (data.live && data.live.context) || data.context || lastContext(thread.messages);
      thread.canResume = !!data.canResume;
      thread.loading = false;
      thread.error = '';
      if (!state.threads[id]) Vue.set(state.threads, id, thread);
    },
    START_TURN(state, { id, data }) {
      const thread = state.threads[id];
      if (!thread) return;
      if (data.userMessage) thread.messages.push(data.userMessage);
      const assistant = data.assistantMessage;
      if (assistant) {
        const index = thread.messages.findIndex((m) => m.id === assistant.id);
        if (index >= 0) thread.messages.splice(index, 1, assistant);
        else thread.messages.push(assistant);
        thread.live = emptyLive(assistant.id);
      }
      thread.canResume = false;
      thread.error = '';
      if (thread.conversation) thread.conversation = { ...thread.conversation, status: 'running' };
    },
    APPLY_EVENT(state, { id, event }) {
      const thread = state.threads[id];
      if (!thread) return;
      if (event.type === 'snapshot') {
        thread.live = { ...emptyLive(event.messageId), ...event, status: event.status || null };
        if (event.context) thread.context = event.context;
        if (!thread.messages.some((m) => m.id === event.messageId)) {
          thread.messages.push({ id: event.messageId, role: 'assistant', content: '', meta: { status: 'running' } });
        }
        return;
      }
      if (event.type === 'done') {
        const { message } = event;
        if (message) {
          const index = thread.messages.findIndex((m) => m.id === message.id);
          const merged = { ...(index >= 0 ? thread.messages[index] : {}), ...message };
          if (index >= 0) thread.messages.splice(index, 1, merged);
          else thread.messages.push(merged);
          if (message.meta && message.meta.context) thread.context = message.meta.context;
        }
        thread.live = null;
        if (event.conversation) thread.conversation = event.conversation;
        thread.canResume = ['interrupted', 'failed'].includes(event.status);
        return;
      }
      const live = thread.live || emptyLive(event.messageId);
      switch (event.type) {
        case 'status':
          live.status = { phase: event.phase, text: event.text };
          if (event.phase !== 'thinking') live.progress = null;
          break;
        case 'step': {
          const index = live.steps.findIndex((s) => s.id === event.step.id);
          if (index >= 0) live.steps.splice(index, 1, event.step);
          else live.steps.push(event.step);
          break;
        }
        case 'draft':
          live.draft += event.text;
          break;
        case 'draft_reset':
          live.draft = '';
          break;
        case 'progress':
          live.progress = { elapsed: event.elapsed, reasoningTokens: event.reasoningTokens };
          break;
        case 'context':
          live.context = event.context;
          thread.context = event.context;
          break;
        default:
          return;
      }
      thread.live = live;
    },
    ADD_EVIDENCE(state, { id, items }) {
      const known = { ...(state.evidence[id] || {}) };
      Object.keys(items).forEach((key) => {
        known[key] = items[key];
      });
      Vue.set(state.evidence, id, known);
    },
  },

  actions: {
    ensureOwner({ state, commit, rootGetters }) {
      const owner = rootGetters['user/userId'] || null;
      if (state.ownerId !== owner) commit('RESET', owner);
    },
    async init({ state, dispatch }) {
      await dispatch('ensureOwner');
      const jobs = [dispatch('refreshStatus')];
      if (!state.capabilities) jobs.push(dispatch('loadCapabilities'));
      else dispatch('refreshCapabilities');
      if (!state.listsLoaded[state.mode]) jobs.push(dispatch('loadList', state.mode));
      await Promise.all(jobs);
    },
    async loadCapabilities({ commit }) {
      try {
        const { data } = await api.getCapabilities();
        commit('SET', { capabilities: data, capabilitiesError: '', capabilitiesCode: '' });
      } catch (error) {
        commit('SET', {
          capabilitiesError: api.errorText(error, 'Сервис ассистента недоступен'),
          capabilitiesCode: api.errorCode(error),
        });
      }
    },
    // Fresh counters (new side work findings) without touching the diagnostic state on a failure.
    async refreshCapabilities({ commit }) {
      try {
        const { data } = await api.getCapabilities();
        commit('SET', { capabilities: data, capabilitiesError: '', capabilitiesCode: '' });
      } catch (error) {
        // The previous capabilities stay; the next opening of the widget shows a real problem.
      }
    },
    // Before the widget is opened: the launcher shows new side work findings to administrators.
    async preload({ state, dispatch }) {
      await dispatch('ensureOwner');
      if (!state.capabilities) await dispatch('refreshCapabilities');
    },
    // «Повторить» on the diagnostic screen.
    async reconnect({ state, dispatch }) {
      await Promise.all([dispatch('loadCapabilities'), dispatch('refreshStatus')]);
      if (!state.capabilitiesError) await dispatch('loadList', state.mode).catch(() => {});
    },
    async refreshStatus({ commit }) {
      try {
        const { data } = await api.getStatus();
        commit('SET', { status: data });
      } catch (error) {
        commit('SET', { status: { error: api.errorText(error, 'Сервис ассистента недоступен') } });
      }
    },
    async setMode({ state, commit, dispatch }, mode) {
      commit('SET', { mode });
      if (!state.listsLoaded[mode]) await dispatch('loadList', mode);
    },
    async loadList({ commit }, mode) {
      try {
        const { data } = await api.listConversations(mode);
        commit('SET_LIST', { mode, list: Array.isArray(data) ? data : [] });
      } catch (error) {
        commit('SET_LIST', { mode, list: [] });
        throw error;
      }
    },
    newConversation({ state, commit }) {
      commit('SET_ACTIVE', { mode: state.mode, id: null });
    },
    async openConversation({ state, commit, dispatch }, id) {
      commit('SET_ACTIVE', { mode: state.mode, id });
      commit('ENSURE_THREAD', id);
      if (state.threads[id].conversation && (state.threads[id].live || subscriptions.has(id))) return;
      await dispatch('reloadThread', id);
    },
    async reloadThread({ commit, dispatch }, id) {
      commit('ENSURE_THREAD', id);
      commit('PATCH_THREAD', { id, patch: { loading: true } });
      try {
        const { data } = await api.getConversation(id);
        commit('LOAD_THREAD', { id, data });
        commit('UPSERT_LIST_ITEM', data.conversation);
        if (data.live) dispatch('subscribe', id);
      } catch (error) {
        const status = error.response && error.response.status;
        if (status === 404) {
          commit('REMOVE_CONVERSATION', id);
          return;
        }
        commit('PATCH_THREAD', { id, patch: { loading: false, error: api.errorText(error, 'Не удалось открыть') } });
      }
    },
    subscribe({ commit, dispatch }, id) {
      if (subscriptions.has(id)) return;
      const handle = api.subscribeEvents(id, {
        onEvent: (event) => {
          if (event.type === 'idle') {
            subscriptions.delete(id);
            dispatch('reloadThread', id);
            return;
          }
          if (event.type === 'resync') return;
          commit('APPLY_EVENT', { id, event });
          if (event.type === 'done') {
            subscriptions.delete(id);
            if (event.conversation) commit('UPSERT_LIST_ITEM', event.conversation);
            dispatch('refreshStatus');
          }
        },
        onError: (error) => {
          subscriptions.delete(id);
          commit('PATCH_THREAD', { id, patch: { error: error.message } });
          dispatch('reloadThread', id);
        },
      });
      subscriptions.set(id, handle);
    },
    // attachments: ids of uploaded files; the text may be empty when there are files.
    async send({ state, commit, dispatch }, { text, attachments = [] }) {
      const { mode } = state;
      const id = state.active[mode];
      if (id) {
        const { data } = await api.sendMessage(id, text, attachments);
        commit('START_TURN', { id, data });
        dispatch('subscribe', id);
        return id;
      }
      const body = { mode: 'chat', text, ...(attachments.length ? { attachments } : {}) };
      const { data } = await api.createConversation(body);
      return dispatch('started', { mode, data });
    },
    async startInvestigation({ commit, dispatch }, body) {
      const { data } = await api.createConversation({ mode: 'investigation', ...body });
      commit('SET', { mode: 'investigation' });
      return dispatch('started', { mode: 'investigation', data });
    },
    started({ commit, dispatch }, { mode, data }) {
      const { id } = data.conversation;
      commit('ENSURE_THREAD', id);
      commit('LOAD_THREAD', { id, data: { conversation: data.conversation, messages: [] } });
      commit('START_TURN', { id, data });
      commit('UPSERT_CONVERSATION', { ...data.conversation, status: 'running' });
      commit('SET_ACTIVE', { mode, id });
      dispatch('subscribe', id);
      return id;
    },
    async resume({ commit, dispatch }, id) {
      const { data } = await api.resumeConversation(id);
      commit('START_TURN', { id, data: { assistantMessage: data && data.assistantMessage } });
      await dispatch('reloadThread', id);
      dispatch('subscribe', id);
    },
    async cancel(context, id) {
      await api.cancelConversation(id);
    },
    async rename({ state, commit }, { id, title }) {
      await api.renameConversation(id, title);
      const thread = state.threads[id];
      const current =
        (thread && thread.conversation) || [...state.lists.chat, ...state.lists.investigation].find((c) => c.id === id);
      if (current) commit('UPSERT_LIST_ITEM', { ...current, title });
    },
    async remove({ commit }, id) {
      await api.deleteConversation(id);
      const handle = subscriptions.get(id);
      if (handle) handle.close();
      subscriptions.delete(id);
      commit('REMOVE_CONVERSATION', id);
    },
    async clear({ state, commit }, mode) {
      await api.clearConversations(mode);
      state.lists[mode].forEach((c) => {
        const handle = subscriptions.get(c.id);
        if (handle) handle.close();
        subscriptions.delete(c.id);
        commit('REMOVE_CONVERSATION', c.id);
      });
      commit('SET_LIST', { mode, list: [] });
    },
    async fetchEvidence({ state, commit }, { id, ids }) {
      const known = state.evidence[id] || {};
      const missing = [...new Set(ids)].filter((eid) => !(eid in known));
      if (!missing.length) return;
      const pending = {};
      missing.forEach((eid) => {
        pending[eid] = { pending: true };
      });
      commit('ADD_EVIDENCE', { id, items: pending });
      try {
        const { data } = await api.getEvidence(id, missing);
        const found = {};
        (data.items || []).forEach((item) => {
          found[item.id] = item;
        });
        missing.forEach((eid) => {
          if (!found[eid]) found[eid] = { unavailable: true };
        });
        commit('ADD_EVIDENCE', { id, items: found });
      } catch (error) {
        const failed = {};
        missing.forEach((eid) => {
          failed[eid] = { error: api.errorText(error, 'Материал не загружен') };
        });
        commit('ADD_EVIDENCE', { id, items: failed });
      }
    },
    async loadComputers({ state, commit }, force = false) {
      if (state.computers && !force) return state.computers;
      const { data } = await api.getComputers();
      commit('SET', { computers: data });
      return data;
    },
    // Counts for the «Память» badge: after the widget opens and after every memory change.
    async loadMemoryCounts({ commit }) {
      try {
        const { data } = await api.listMemory({ status: 'pending' });
        commit('SET', { memoryCounts: data.counts || null });
      } catch (error) {
        commit('SET', { memoryCounts: null });
      }
    },
    closeStreams() {
      closeAll();
    },
  },
};
